import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import debounce from "lodash.debounce";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mantine/dates";
import { Popover } from "@mantine/core";
import SearchIcon from "@mui/icons-material/Search";
import PlaceIcon from "@mui/icons-material/Place";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import Nav from "../NavBar/Nav";
import FooterNew from "../NavBar/FooterNew";
import base_url from "../../api/bootapi";
import { formatDate, formatDateRange } from "./experienceUtils";
import heroImage from "../../assets/tour-banner2.jpg";
import catParis from "../../assets/split-1.jpg";
import catDubai from "../../assets/split-2.jpg";
import catBali from "../../assets/split-3.jpg";
import catRome from "../../assets/split-4.jpg";
import "./Experiences.css";

const POPULAR_DESTINATIONS = ["Paris", "Dubai", "New York", "Rome", "Bali", "Bangkok"];

const CATEGORY_TILES = [
  { name: "Paris", label: "Paris, France", image: catParis },
  { name: "Dubai", label: "Dubai, UAE", image: catDubai },
  { name: "Bali", label: "Bali, Indonesia", image: catBali },
  { name: "Rome", label: "Rome, Italy", image: catRome },
];

export default function ExperienceSearch() {
  const navigate = useNavigate();

  const [destinationQuery, setDestinationQuery] = useState("");
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [showDestDropdown, setShowDestDropdown] = useState(false);
  const [destLoading, setDestLoading] = useState(false);
  const [autoSearching, setAutoSearching] = useState(false);

  const [dateRange, setDateRange] = useState([null, null]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const destWrapperRef = useRef(null);
  // Tracks the most recent query we asked for, so an older, slower
  // response can't clobber the results of a newer one.
  const latestQueryRef = useRef("");

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (destWrapperRef.current && !destWrapperRef.current.contains(e.target)) {
        setShowDestDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // IMPORTANT: debounce() must be created ONCE and reused, not rebuilt on
  // every render. Previously this was declared as a plain `const` in the
  // component body, so every keystroke (which triggers a re-render via
  // setDestinationQuery) produced a brand-new debounced function. Each of
  // those independent instances still fired its own timer 400ms later,
  // so instead of debouncing to one request, every keystroke fired its
  // own API call. useRef keeps a single debounced instance alive across
  // renders so the 400ms debounce actually works.
  const fetchDestinationsRef = useRef(
    debounce(async (query, onDone) => {
      if (!query || query.trim().length < 2) {
        setDestinationOptions([]);
        return;
      }
      latestQueryRef.current = query;
      setDestLoading(true);
      try {
        const response = await axios.get(
          `${base_url.api1}/tourapp/viator/destinations?search=${encodeURIComponent(
            query
          )}&page=1&count=10`
        );
        // Guard against out-of-order responses: if the user has since
        // typed something else, ignore this now-stale result.
        if (latestQueryRef.current !== query) return;
        const results =
          response.data && response.data.status ? response.data.destinations || [] : [];
        setDestinationOptions(results);
        if (onDone) onDone(results);
      } catch (error) {
        if (latestQueryRef.current !== query) return;
        console.error("Error fetching destinations:", error);
        setDestinationOptions([]);
        if (onDone) onDone([]);
      } finally {
        if (latestQueryRef.current === query) setDestLoading(false);
      }
    }, 400)
  );
  const fetchDestinations = fetchDestinationsRef.current;

  const handleDestinationInputChange = (e) => {
    const value = e.target.value;
    setDestinationQuery(value);
    setSelectedDestination(null);
    setShowDestDropdown(true);
    fetchDestinations(value);
  };

  const handleSelectDestination = (destination) => {
    setSelectedDestination(destination);
    setDestinationQuery(destination.name);
    setShowDestDropdown(false);
  };

  const goToResults = (destination) => {
    const params = new URLSearchParams({
      destination_id: String(destination.destination_id),
      name: destination.name,
      currency: destination.default_currency_code || "USD",
    });
    if (dateRange[0] && dateRange[1]) {
      params.set("start", formatDate(dateRange[0]));
      params.set("end", formatDate(dateRange[1]));
    }
    navigate(`/experiences/results?${params.toString()}`, {
      state: { destination, dateRange },
    });
  };

  const handleSearch = () => {
    if (!selectedDestination) {
      setErrorMsg("Please choose a place or activity from the list.");
      return;
    }
    setErrorMsg("");
    goToResults(selectedDestination);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  // Popular chip / category tile: fill in the field, look the place up, and
  // jump straight to results with the best match — no dead click needed.
  const handleQuickPick = (name) => {
    setErrorMsg("");
    setDestinationQuery(name);
    setAutoSearching(true);
    fetchDestinations(name, (results) => {
      setAutoSearching(false);
      if (results && results.length) {
        const bestMatch =
          results.find((d) => d.name.toLowerCase() === name.toLowerCase()) || results[0];
        setSelectedDestination(bestMatch);
        setDestinationQuery(bestMatch.name);
        goToResults(bestMatch);
      } else {
        setErrorMsg(`We couldn't find experiences for "${name}" right now.`);
      }
    });
  };

  const dateDisplay =
    dateRange[0] && dateRange[1] ? formatDateRange(dateRange[0], dateRange[1]) : "";

  return (
    <div className="exp-scope">
      <div style={{ position: "relative" }}>
        <Nav />
      </div>

      <div className="exp-hero" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="exp-hero-overlay" />
        <div className="exp-hero-content">
          <span className="exp-hero-eyebrow">Tours &amp; Experiences</span>
          <h1 className="exp-hero-title">Go beyond the itinerary</h1>
          <p className="exp-hero-subtitle">
            Skip-the-line tickets, guided tours, and local experiences in every city
            Linsoa flies you to — search, compare and book, all in one place.
          </p>

          <div className="exp-search-card">
            <div className="exp-search-field" ref={destWrapperRef}>
              <label>Where to?</label>
              <input
                type="text"
                placeholder="Search a city, landmark or activity"
                value={destinationQuery}
                onChange={handleDestinationInputChange}
                onFocus={() => setShowDestDropdown(true)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
              />
              {showDestDropdown && destinationQuery.trim().length >= 2 && (
                <div className="exp-dest-dropdown">
                  {destLoading && (
                    <div className="exp-dest-option exp-dest-loading">Searching...</div>
                  )}
                  {!destLoading && destinationOptions.length === 0 && (
                    <div className="exp-dest-option exp-dest-empty">
                      No matching places found
                    </div>
                  )}
                  {!destLoading &&
                    destinationOptions.map((dest, index) => {
                      // The row that matches what was typed reads as a real
                      // place (pin icon, highlighted) — every other partial
                      // match reads as a related suggestion (ticket icon),
                      // mirroring the reference design's mix of icon types.
                      const isExactMatch =
                        dest.name.trim().toLowerCase() ===
                        destinationQuery.trim().toLowerCase();
                      return (
                        <div
                          key={dest.destination_id || `${dest.name}-${index}`}
                          className={`exp-dest-option${
                            isExactMatch ? " exp-dest-option-highlight" : ""
                          }`}
                          onClick={() => handleSelectDestination(dest)}
                        >
                          {isExactMatch ? (
                            <PlaceIcon fontSize="small" />
                          ) : (
                            <ConfirmationNumberOutlinedIcon fontSize="small" />
                          )}
                          <div>
                            <div className="exp-dest-name">{dest.name}</div>
                            <div className="exp-dest-type">
                              {dest.parent_destination_name || dest.type}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  {!destLoading && (
                    <div
                      className="exp-dest-option exp-dest-option-search"
                      onClick={() => handleQuickPick(destinationQuery)}
                    >
                      <SearchIcon fontSize="small" />
                      <div className="exp-dest-name">
                        Search for {destinationQuery}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <Popover
              opened={showDatePicker}
              onChange={setShowDatePicker}
              position="bottom-start"
              shadow="md"
              withinPortal
            >
              <Popover.Target>
                <div
                  className="exp-search-field exp-search-when"
                  onClick={() => setShowDatePicker((o) => !o)}
                >
                  <label>When</label>
                  <input type="text" readOnly value={dateDisplay} placeholder="Any dates" />
                </div>
              </Popover.Target>
              <Popover.Dropdown>
                <DatePicker
                  type="range"
                  value={dateRange}
                  onChange={setDateRange}
                  minDate={new Date()}
                  allowSingleDateInRange
                />
              </Popover.Dropdown>
            </Popover>

            <button
              className="exp-search-btn"
              onClick={handleSearch}
              disabled={autoSearching}
              aria-label="Search experiences"
            >
              <SearchIcon />
            </button>
          </div>
          {errorMsg && <div className="exp-error-msg">{errorMsg}</div>}

          <div className="exp-hero-trust">
            <span>
              <VerifiedIcon fontSize="small" /> Verified local operators
            </span>
            <span>
              <EventAvailableIcon fontSize="small" /> Free cancellation on most experiences
            </span>
            <span>
              <SupportAgentIcon fontSize="small" /> Booked through Linsoa, end to end
            </span>
          </div>
        </div>
      </div>

      {/* <div className="exp-popular">
        <div className="exp-popular-label">Travelers are searching for</div>
        <div className="exp-popular-chips">
          {POPULAR_DESTINATIONS.map((name) => (
            <button key={name} className="exp-chip" onClick={() => handleQuickPick(name)}>
              {name}
            </button>
          ))}
        </div>
      </div> */}

      <div className="exp-container">
        <div className="exp-section-heading">
          <h2>Popular right now</h2>
          <p>Hand-picked destinations where travelers are booking the most experiences this season.</p>
        </div>
        <div className="exp-category-grid">
          {CATEGORY_TILES.map((tile) => (
            <div
              key={tile.name}
              className="exp-category-card"
              onClick={() => handleQuickPick(tile.name)}
            >
              <img src={tile.image} alt={tile.label} />
              <span>{tile.label}</span>
            </div>
          ))}
        </div>
      </div>

      <FooterNew />
    </div>
  );
}
